import { useState } from 'react'
import { levelOf } from '@kronos/domain'
import { repository } from '../../data'
import type { MachineView, View } from './derive'
import { describePath } from './derive'
import { GiveAccess } from './GiveAccess'
import { ago } from './format'
import { JoinDevice } from './JoinDevice'
import { Chip, NetDot, useArm, useWrite } from './parts'

/** One machine: is it up, how to reach it, and who can SSH in — with the reason for each person. */
export function MachinePanel({ view, machine, onClose }: { view: View; machine: MachineView; onClose: () => void }): JSX.Element {
  const [modal, setModal] = useState<'join' | 'direct' | null>(null)
  const arm = useArm()
  const retire = useWrite(() => repository.retireMachine(machine.id).then((r) => (onClose(), r)), () => `Retired ${machine.name}; nobody can reach it now`)
  const people = [...machine.access.entries()]
    .map(([userId, paths]) => ({ person: view.person(userId), paths }))
    .filter((x) => x.person)
    .sort((a, b) => (a.person?.name ?? '').localeCompare(b.person?.name ?? ''))
  const state =
    machine.net === 'online'
      ? 'Online now'
      : machine.net === 'offline'
        ? `Offline${machine.device?.lastSeen ? ` · seen ${ago(machine.device.lastSeen)}` : ''}`
        : 'Not on the network yet'

  return (
    <section className="cx-panel cx-panel--padded" aria-label={machine.name}>
      <div className="row" style={{ justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div className="row" style={{ gap: 10 }}>
          <NetDot state={machine.net} />
          <span className="stack" style={{ gap: 2 }}>
            <span className="cx-mono cx-title">{machine.name}</span>
            <span className="muted cx-small">{state}</span>
          </span>
        </div>
        <button aria-label="Close" onClick={onClose}>×</button>
      </div>

      <dl className="cx-facts">
        <dt>Address</dt>
        <dd className="cx-mono">{machine.device?.addresses[0] ?? '—'}</dd>
        <dt>Log in</dt>
        <dd className="cx-mono">ssh {machine.sshUser}@{machine.device?.name ?? machine.tag.slice(4)}</dd>
        <dt>Tag</dt>
        <dd className="cx-mono">{machine.tag}</dd>
        {machine.robot ? (
          <>
            <dt>In Kronos</dt>
            <dd>{machine.robot.fleetNumber}{machine.robot.site ? ` · ${machine.robot.site}` : ''}</dd>
          </>
        ) : null}
        <dt>Groups</dt>
        <dd className="cx-chips">{machine.groupIds.map((id) => <Chip key={id} tone="plain">{view.group(id)?.name}</Chip>)}</dd>
      </dl>

      <span className="field-label">Who can SSH in, and why · {people.length}</span>
      {people.length === 0 ? <p className="muted">Nobody yet. Press “Give access”, or a square on Access.</p> : null}
      <ul className="cx-list cx-list--scroll">
        {people.map(({ person, paths }) => (
          <li key={person?.id} className="cx-why">
            <span className="row" style={{ gap: 8, justifyContent: 'space-between' }}>
              <strong>{person?.name}</strong>
              {levelOf(paths) === 'restricted' ? <Chip tone="plain">restricted</Chip> : null}
            </span>
            <span className="muted cx-small">{paths.map((p) => describePath(view, p)).join(' · ')}</span>
          </li>
        ))}
      </ul>

      <div className="cx-actions">
        <button className="primary" onClick={() => setModal('join')}>{machine.net === 'not_joined' ? 'Join it to the network' : 'Join a device'}</button>
        <button onClick={() => setModal('direct')}>Give access</button>
        <button className={arm.armed('retire') ? 'danger is-armed' : 'danger'} disabled={retire.pending} onClick={() => arm.press('retire', () => retire.run())}>
          {arm.armed('retire') ? 'Press again: retire' : 'Retire'}
        </button>
      </div>
      {modal === 'join' ? <JoinDevice machine={machine} onClose={() => setModal(null)} /> : null}
      {modal === 'direct' ? <GiveAccess view={view} preset={{ machineId: machine.id }} onClose={() => setModal(null)} /> : null}
    </section>
  )
}
